import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCheck, Loader2, MessageCircle, Send, X } from 'lucide-react';
import backendApi from '../../services/backendApi';

const messageTime = value => new Intl.DateTimeFormat('fr-FR', {
  hour: '2-digit',
  minute: '2-digit',
}).format(new Date(value));

function TeamChatPanel({ member, currentUserId, onClose, onConversationRead }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const loadConversation = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const history = await backendApi.getChatConversation(member.id);
      setMessages(history);
      setError('');
      await backendApi.markChatConversationRead(member.id);
      onConversationRead(member.id);
    } catch (loadError) {
      if (!quiet) setError(loadError.message);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [member.id, onConversationRead]);

  useEffect(() => {
    loadConversation();
    const timer = window.setInterval(() => loadConversation({ quiet: true }), 3000);
    const receiveMessage = event => {
      const message = event.detail;
      if (message && (message.sender_id === member.id || message.recipient_id === member.id)) {
        loadConversation({ quiet: true });
      }
    };
    window.addEventListener('team-chat:message', receiveMessage);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('team-chat:message', receiveMessage);
    };
  }, [loadConversation, member.id]);

  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
    const handleEscape = event => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const sendMessage = async event => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || sending) return;
    setSending(true);
    setError('');
    try {
      const message = await backendApi.sendChatMessage(member.id, body);
      setMessages(current => current.some(item => item.id === message.id) ? current : [...current, message]);
      setDraft('');
    } catch (sendError) {
      setError(sendError.message);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  return (
    <aside className="tv-chat-panel" role="dialog" aria-modal="false" aria-labelledby="tv-chat-title">
      <header className="tv-chat-header">
        <span className="tv-chat-header-icon" aria-hidden="true"><MessageCircle /></span>
        <div>
          <h2 id="tv-chat-title">{member.name}</h2>
          <p>{member.online ? 'En ligne' : 'Hors ligne · le message sera conservé'}</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Fermer la conversation"><X /></button>
      </header>

      <div className="tv-chat-messages" aria-live="polite" aria-busy={loading}>
        {loading ? (
          <div className="tv-chat-state"><Loader2 className="is-spinning" /> Chargement…</div>
        ) : messages.length === 0 ? (
          <div className="tv-chat-empty"><MessageCircle /><strong>Démarrez la conversation</strong><span>Votre premier message apparaîtra ici.</span></div>
        ) : messages.map(message => {
          const mine = message.sender_id === currentUserId;
          return (
            <article className={`tv-chat-message ${mine ? 'is-mine' : 'is-theirs'}`} key={message.id}>
              <p>{message.body}</p>
              <footer>
                <time dateTime={message.created_at}>{messageTime(message.created_at)}</time>
                {mine && message.read_at && <CheckCheck aria-label="Lu" />}
              </footer>
            </article>
          );
        })}
        <div ref={endRef} />
      </div>

      {error && <p className="tv-chat-error" role="alert"><AlertCircle /> {error}</p>}
      <form className="tv-chat-compose" onSubmit={sendMessage}>
        <textarea
          ref={inputRef}
          value={draft}
          maxLength={1000}
          rows={1}
          aria-label={`Écrire à ${member.name}`}
          placeholder="Écrire un message…"
          onChange={event => setDraft(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && !event.shiftKey) sendMessage(event);
          }}
        />
        <button type="submit" disabled={!draft.trim() || sending} aria-label="Envoyer le message">
          {sending ? <Loader2 className="is-spinning" /> : <Send />}
        </button>
      </form>
    </aside>
  );
}

export default TeamChatPanel;
